import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  X,
  FileText,
  User,
  Building,
  CreditCard,
  Info,
  Send,
  Sparkles,
  Zap,
  Plus,
  Eye,
} from "lucide-react";

// UI Components
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Textarea } from "components/ui/textarea";
import { Badge } from "components/ui/badge";
import { SafeDatePicker } from "components/ui/safe-date-picker";
import { Toaster } from "components/ui/sonner";
import { toast } from "sonner";
import Tooltip from "components/ui/tooltip";
import ErrorBoundary from "components/ui/error-boundary";
import { TagInput } from "components/ui/tag-input";
import { Combobox, ComboboxOption } from "components/ui/combobox";

// Dialog Components
import NewPracticeDialog from "./components/NewPracticeDialog";
import AddHandlerDialog from "./components/AddHandlerDialog";
import { CallScriptPreviewDialog } from "components/CallScriptManager";
import { getCallScripts, type CallScriptResponse } from "utils/callScriptsApi";

// Constants and Utils
import {
  NETWORK,
  NETWORK_OPTIONAL,
  REQUEST_TYPES_DENTAL,
  REQUEST_TYPES_MEDICAL,
  Option,
  PLACE_OF_SERVICE_OPTIONS,
  SPECIALIST_OPTIONS,
  SPECIALIST_OPTIONS_DENTAL,
  REQUEST_TYPES_CAMBER,
  REQUEST_TYPES_COPILOT,
} from "utils/constants";
import { TOOLTIPS } from "utils/tooltips";

// Actions and API
import { NEW_REQUEST_PAGE } from "utils/reducers";
import { Mixpanel } from "utils/Analytics";
import baseApi from "utils/axios-instance";
import { RequestSchema } from "utils/validationSchema";
import { RequestInfo } from "pages/MVP/RequestDetailsPage/components/RequestInfo";
import { ProfileInfo } from "utils/ProfileInfo";
import { CurrentUserInfo } from "pages/MVP/CurrentUserInfo";
import { isCamberWhitelabel } from "utils/utils";
import { convertToString, safeStringValue } from "utils/form-helpers";
import { safeFormatDateForAPI } from "utils/safe-parsing";
import { STEDI_PAYOR_OTHER, STEDI_PAYORS } from "utils/stedi_payors";

interface NewRequestPageProps {
  reloadPage?: any;
  debugUser?: any;
  profileInfo?: ProfileInfo | undefined;
  currentUser?: CurrentUserInfo | undefined;
  refreshProfileInfo: (showLoading?: boolean) => Promise<void>;
}

function NewRequestPage({
  reloadPage,
  debugUser,
  profileInfo,
  currentUser,
  refreshProfileInfo,
}: NewRequestPageProps) {
  const form = useForm({
    resolver: yupResolver(RequestSchema),
  });

  const { control, handleSubmit, watch, setValue, reset } = form;

  const dispatch = useDispatch();

  // Track when this page becomes visible to handle form reset
  const showNewRequestPage = useSelector(
    (state: any) => state.showNewRequestPage.show
  );

  // Watch for changes in the form
  const typeField = watch("type");
  const practiceIdField = watch("practice_id");
  const billingProviderIdField = watch("billing_provider_id");
  const renderingProviderIdField = watch("rendering_provider_id");
  const transferPhoneNumberField = watch("transfer_to_rep_number");
  const callScriptIdField = watch("call_script_id");
  const insuranceNameField = watch("insurance_name");

  // Watch all form values for localStorage saving
  const watchedValues = watch();

  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const [practicesOptionsList, setPracticesOptionsList] = useState<
    Array<Option>
  >([]);
  const [providersOptionsList, setProvidersOptionsList] = useState<
    Array<Option>
  >([]);
  const [showBulkMessage, setShowBulkMessage] = useState(true);
  const [useSameProvider, setUseSameProvider] = useState<boolean>(true);

  // Dialog states
  const [showNewPracticeDialog, setShowNewPracticeDialog] = useState(false);
  const [showAddHandlerDialog, setShowAddHandlerDialog] = useState(false);
  const [currentPracticeId, setCurrentPracticeId] = useState<string>("");
  const [showCallScriptPreview, setShowCallScriptPreview] = useState(false);

  // Call script state
  const [callScripts, setCallScripts] = useState<CallScriptResponse[]>([]);
  const [loadingCallScripts, setLoadingCallScripts] = useState(false);

  // Insurance phone numbers state
  const [insurancePhoneNumbers, setInsurancePhoneNumbers] = useState<Record<string, string>>({});

  // Format phone number for display (e.g., +18005558234 -> +1 800 555 8234)
  const formatPhoneNumber = (phone: string): string => {
    // Remove any existing formatting
    const cleaned = phone.replace(/\D/g, '');

    // Format as +1 XXX XXX XXXX
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      return `+1 ${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
    }

    // If format doesn't match, return original
    return phone;
  };

  // Payor options from hardcoded list with phone numbers
  const payorOptions = useMemo<ComboboxOption[]>(() => {
    return STEDI_PAYORS.map((payor) => {
      const phoneNumber = insurancePhoneNumbers[payor];
      const label = phoneNumber ? `${payor} (${formatPhoneNumber(phoneNumber)})` : payor;
      return {
        label,
        value: payor,
      };
    });
  }, [insurancePhoneNumbers]);

  // Unify prefill data source
  const [dataToPrefill, setDataToPrefill] = useState<
    Partial<RequestInfo> | undefined
  >();

  // Reset form when page becomes visible and no saved data exists
  useEffect(() => {
    if (showNewRequestPage) {
      const savedDataJSON = localStorage.getItem("newRequestFormData");
      console.log("savedDataJSON", savedDataJSON);
      if (savedDataJSON) {
        const savedData = JSON.parse(savedDataJSON);
        console.log("savedData", savedData);
        setDataToPrefill(savedData);

        // Check if saved data has different providers
        if (
          savedData.billing_provider_id &&
          savedData.rendering_provider_id &&
          savedData.billing_provider_id !== savedData.rendering_provider_id
        ) {
          setUseSameProvider(false);
        }
      } else {
        // If no saved data exists, reset the form completely
        // This handles the case where user submitted a form and localStorage was cleared
        reset();
        setUseSameProvider(true);
        setDataToPrefill(undefined);
      }
    }
  }, [showNewRequestPage, reset]);

  // Fields that should NOT be saved to localStorage (patient PII)
  const excludedFields = useMemo(() => [
    "patient_name",
    "member_id",
    "dob",
    "group_id",
    "patient_address",
  ], []);

  // These fields should be saved even if they're false or empty arrays
  const eligibilityFields = useMemo(() => [
    "place_of_service",
    "is_specialist",
    "eligibility_search_queries",
    "call_script_id",
  ], []);

  // Save form data to localStorage whenever form values change
  useEffect(() => {
    if (Object.keys(watchedValues).length > 0) {
      // Filter out empty/undefined values, but preserve false/empty arrays for eligibility fields
      const dataToSave = Object.fromEntries(
        Object.entries(watchedValues).filter(([key, value]) => {
          // Skip excluded fields
          if (excludedFields.includes(key)) {
            return false;
          }

          if (eligibilityFields.includes(key)) {
            return value !== undefined && value !== null;
          }

          // For other fields, use original filtering
          return value !== undefined && value !== null && value !== "";
        })
      );
      if (Object.keys(dataToSave).length > 0) {
        localStorage.setItem("newRequestFormData", JSON.stringify(dataToSave));
      }
    }
  }, [watchedValues, excludedFields, eligibilityFields]);

  const handleSetPracticeID = useCallback((practice_id: string) => {
    setCurrentPracticeId(practice_id);
  }, []);

  // Clear conditional fields when type changes
  useEffect(() => {
    setValue("claims_date_of_service", undefined);
    setValue("billed_amount", undefined);
    setValue("eligibility_search_queries", undefined);
    setValue("call_script_id", undefined);
    setValue("place_of_service", undefined);
    setValue("is_specialist", undefined);
    setValue("insurance_in_network", undefined);
    setValue("diagnosis_codes", undefined);
    setValue("procedure_codes", undefined);
  }, [typeField, setValue]);

  // Prefill form with data from local storage
  useEffect(() => {
    if (!dataToPrefill) return;

    const allData = dataToPrefill as any;

    // First pass: Set all values without validation to avoid race conditions
    Object.keys(allData).forEach((key) => {
      const value = allData[key];
      if (value !== null && value !== undefined) {
        setValue(key as any, value, { shouldValidate: false });
      }
    });

    // Second pass: Trigger validation after a delay to ensure form is fully hydrated
    setTimeout(() => {
      Object.keys(allData).forEach((key) => {
        const value = allData[key];
        if (value !== null && value !== undefined) {
          console.log("setting value", key, value);
          setValue(key as any, value, { shouldValidate: true });
        }
      });
    }, 100);
  }, [dataToPrefill, setValue]);

  // Load practice options
  useEffect(() => {
    const loadPracticeOptions = (profileInfo: ProfileInfo | undefined) => {
      if (profileInfo == null) return;
      let practicesToLoad = profileInfo.practices;
      if (practicesToLoad == null) return;
      let practiceOptions: Array<Option> = [];
      if (practicesToLoad.length === 0)
        return setPracticesOptionsList(practiceOptions);
      practicesToLoad.forEach((val) => {
        practiceOptions.push({ label: val.name, value: val.id });
      });
      practiceOptions.push({ label: "Add new practice", value: "OTHER" });
      setPracticesOptionsList(practiceOptions);
    };

    loadPracticeOptions(profileInfo);
  }, [profileInfo]);

  // Load provider options
  useEffect(() => {
    const loadProviderOptions = (profileInfo: ProfileInfo | undefined) => {
      if (profileInfo == null) return;
      let practices = profileInfo.practices;
      if (practices == null) return;
      let providerOptions: Array<Option> = [];
      if (practices.length === 0)
        return setProvidersOptionsList(providerOptions);
      practices.forEach((val) => {
        if (val.id === practiceIdField) {
          val.providers.forEach((sub) => {
            providerOptions.push({ label: sub.name, value: sub.id });
          });
        }
      });
      providerOptions.push({ label: "Add new provider", value: "OTHER" });
      setProvidersOptionsList(providerOptions);
    };
    loadProviderOptions(profileInfo);
    setValue("billing_provider_id", "");
    setValue("rendering_provider_id", "");
  }, [practiceIdField, profileInfo, setValue]);

  // Handle new onboard modal trigger
  useEffect(() => {
    const practiceIdToUse =
      billingProviderIdField === "OTHER"
        ? practiceIdField
        : practiceIdField === "OTHER"
          ? "OTHER"
          : practiceIdField;

    if (
      practiceIdToUse === "OTHER" ||
      billingProviderIdField === "OTHER" ||
      renderingProviderIdField === "OTHER"
    ) {
      // Check if trying to add provider without practice selected
      if (
        (billingProviderIdField === "OTHER" ||
          renderingProviderIdField === "OTHER") &&
        (!practiceIdField || practiceIdField === "")
      ) {
        toast.error(
          "Please select a practice first before adding a new provider"
        );
        setValue("billing_provider_id", "");
        setValue("rendering_provider_id", "");
        return;
      }

      handleSetPracticeID(practiceIdToUse);
      setShowNewPracticeDialog(true);

      // Reset the "OTHER" selects to avoid re-triggering
      if (practiceIdField === "OTHER") {
        setValue("practice_id", "");
      }
      if (billingProviderIdField === "OTHER") {
        setValue("billing_provider_id", "");
      }
      if (renderingProviderIdField === "OTHER") {
        setValue("rendering_provider_id", "");
      }
    }
  }, [
    practiceIdField,
    billingProviderIdField,
    renderingProviderIdField,
    setValue,
    handleSetPracticeID,
  ]);

  // Handle transfer phone number modal trigger
  useEffect(() => {
    if (transferPhoneNumberField === "OTHER") {
      setShowAddHandlerDialog(true);
      // Reset the select field to avoid triggering again
      setValue("transfer_to_rep_number", "");
    }
  }, [transferPhoneNumberField, setValue]);


  // Fetch insurance phone numbers when component mounts
  useEffect(() => {
    const fetchInsurancePhoneNumbers = async () => {
      try {
        const response = await baseApi.post(
          "/dashboard/insurance-phone-numbers",
          STEDI_PAYORS,
          { withCredentials: true }
        );
        setInsurancePhoneNumbers(response.data);
      } catch (error) {
        console.error("Error fetching insurance phone numbers:", error);
        // Silently fail - we can still show insurance names without phone numbers
      }
    };

    fetchInsurancePhoneNumbers();
  }, []);

  // Fetch call scripts when type is ELIGIBILITY_SEARCH and team_id is available
  useEffect(() => {
    if (typeField === "ELIGIBILITY_SEARCH" && currentUser?.team_id) {
      setLoadingCallScripts(true);
      getCallScripts(currentUser.team_id)
        .then((scripts) => {
          const visibleScripts = scripts.filter((s) => {
            const lower = (s.name ?? "").toLowerCase();
            return !lower.includes("auto-generated");
          });
          setCallScripts(visibleScripts);
        })
        .catch((err: any) => {
          console.error("Error fetching call scripts:", err);
          setCallScripts([]);
        })
        .finally(() => {
          setLoadingCallScripts(false);
        });
    } else {
      setCallScripts([]);
    }
  }, [typeField, currentUser?.team_id]);

  // Clear eligibility search queries when switching away from ELIGIBILITY_SEARCH type
  useEffect(() => {
    if (typeField !== "ELIGIBILITY_SEARCH") {
      setValue("eligibility_search_queries", undefined);
    }
  }, [typeField, setValue]);

  // Clear eligibility search if type is not eligibility search
  useEffect(() => {
    if (typeField !== "ELIGIBILITY_SEARCH") {
      setValue("eligibility_search_queries", undefined);
    }
  }, [typeField, setValue]);

  // Populate additional_info from selected call script when a call script is selected
  useEffect(() => {
    if (callScriptIdField && callScripts.length > 0) {
      const selectedScript = callScripts.find(
        (script) => script.call_script_id === callScriptIdField
      );
      if (selectedScript?.additional_info) {
        setValue("additional_info", selectedScript.additional_info);
      }
    }
  }, [callScriptIdField, callScripts, setValue]);

  // Handle "use same provider" checkbox
  useEffect(() => {
    if (useSameProvider && billingProviderIdField) {
      setValue("rendering_provider_id", billingProviderIdField);
    }
  }, [useSameProvider, billingProviderIdField, setValue]);

  // Update checkbox state when rendering provider is manually changed (but allow manual unchecking)
  useEffect(() => {
    if (
      renderingProviderIdField &&
      billingProviderIdField &&
      renderingProviderIdField === billingProviderIdField &&
      !useSameProvider
    ) {
      setUseSameProvider(true);
    }
  }, [renderingProviderIdField, billingProviderIdField, useSameProvider]);

  // Helper function to filter out empty string values from option arrays and remove duplicates
  const filterValidOptions = (options: Array<Option>) => {
    const filtered = options.filter((option) => option.value !== "");

    // Remove duplicates based on both value and label
    const uniqueOptions = filtered.filter(
      (option, index, self) =>
        index ===
        self.findIndex(
          (o) => o.value === option.value && o.label === option.label
        )
    );

    return uniqueOptions;
  };

  // Safe date change handler
  const handleDateChange = (field: any) => (date: Date | undefined) => {
    if (date) {
      const formattedDate = safeFormatDateForAPI(date);
      field.onChange(formattedDate || "");
    } else {
      field.onChange("");
    }
  };

  const onSubmit = async (data: any) => {
    setIsSubmitDisabled(true);
    if (data.insurance_in_network === "Unknown") {
      data.insurance_in_network = null;
    }

    // Validate call_script_id for ELIGIBILITY_SEARCH type
    if (
      data.type === "ELIGIBILITY_SEARCH" &&
      !data.call_script_id
    ) {
      toast.error("Please select a call script");
      setIsSubmitDisabled(false);
      return;
    }

    // Clear eligibility_search_queries when using call scripts
    data.eligibility_search_queries = undefined;

    // Map insurance_name (payor name from combobox) to payor_name field
    if (data.insurance_name) {
      data.payor_name = data.insurance_name;
      data.insurance_name = 'OTHER'; // For backwards compatibility.
    }

    try {
      await baseApi.post("/dashboard/new_inquiry", data, {
        withCredentials: true,
      });
      Mixpanel.track("New request submitted", { $request_type: data.type });
      toast.success("Request submitted successfully!");

      // Clear localStorage after successful submission
      localStorage.removeItem("newRequestFormData");

      // Navigate back to dashboard and refresh the table
      setTimeout(() => {
        dispatch({ type: NEW_REQUEST_PAGE.hide });
        if (reloadPage) reloadPage(true); // Reload with loading overlay
      }, 500);
    } catch (error: any) {
      Mixpanel.track("Error: New Request", { error: error, data: data });
      const message = error.response?.data?.detail || error.response?.data;
      console.warn(message);
      toast.error(message || "Failed to submit request");
    }
    setIsSubmitDisabled(false);
  };

  // Get options based on specialty using useMemo to prevent unnecessary re-computation
  const { requestTypes } =
    useMemo(() => {
      let requestTypes: Array<Option> = [];

      switch (profileInfo?.specialty) {
        case "MEDICAL":
          requestTypes = REQUEST_TYPES_MEDICAL;
          break;
        case "DENTAL":
          requestTypes = REQUEST_TYPES_DENTAL;
          break;
        case "MEDICAL_AND_DENTAL":
          requestTypes = REQUEST_TYPES_DENTAL;
          break;
        case "MENTAL_HEALTH":
          if (isCamberWhitelabel()) {
            requestTypes = REQUEST_TYPES_CAMBER;
          } else {
            requestTypes = REQUEST_TYPES_MEDICAL;
          }
          break;
        default:
          break;
      }

      // Override for copilot customers
      if (profileInfo?.is_copilot) {
        requestTypes = REQUEST_TYPES_COPILOT;
      }

      return { requestTypes };
    }, [
      profileInfo?.specialty,
      profileInfo?.is_copilot,
    ]);

  var specialistOptions = SPECIALIST_OPTIONS;
  if (profileInfo?.specialty === "DENTAL") {
    specialistOptions = SPECIALIST_OPTIONS_DENTAL;
  }

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error("NewRequestPage Error:", error, errorInfo);
        // Track the error for debugging
        if (typeof window !== "undefined" && (window as any).Mixpanel) {
          (window as any).Mixpanel.track("Error: NewRequestPage Render", {
            error: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
          });
        }
      }}
    >
      <div className="w-full h-full">
        <Toaster richColors position="top-center" />
        <div className="p-4 max-w-5xl mx-auto">
          {/* Compact Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="relative">
                <div className="p-2.5 bg-gradient-to-br from-[#005151] to-[#004040] rounded-lg shadow-lg">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  New Request
                </h1>
                <p className="text-sm text-gray-500">
                  AI-powered healthcare inquiry
                </p>
              </div>
              <div className="ml-auto">
                <div className="flex items-center space-x-1 px-3 py-1 bg-emerald-50 rounded-full">
                  <Zap className="h-3 w-3 text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-700">
                    Smart Form
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bulk CSV Message */}
          {showBulkMessage && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <span className="text-sm text-blue-800">
                  Bulk submissions available{" "}
                  <Link
                    to="/dashboard/csv"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline hover:no-underline"
                  >
                    here
                  </Link>
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBulkMessage(false)}
                className="h-6 w-6 p-0 hover:bg-blue-100"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Compact Form Grid */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <Building className="h-4 w-4 text-[#005151]" />
                    <CardTitle className="text-lg font-semibold text-gray-900">
                      Practice & Provider
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    <FormField
                      control={control}
                      name="practice_id"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Practice
                            <Tooltip text={TOOLTIPS.PRACTICE} />
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-10 ${fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                                  }`}
                              >
                                <SelectValue placeholder="Select practice" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-64 overflow-y-auto">
                              {practicesOptionsList.map((option) => (
                                <SelectItem
                                  key={convertToString(option.value)}
                                  value={convertToString(option.value)}
                                  className={
                                    option.value === "OTHER"
                                      ? "border-t border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 text-emerald-700 font-medium"
                                      : ""
                                  }
                                >
                                  {option.value === "OTHER" ? (
                                    <div className="flex items-center gap-2">
                                      <Plus className="h-4 w-4" />
                                      <span>{option.label}</span>
                                    </div>
                                  ) : (
                                    option.label
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={control}
                        name="billing_provider_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                              Billing Provider
                              <Tooltip text={TOOLTIPS.BILLING_PROVIDER} />
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-10 ${fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                    }`}
                                >
                                  <SelectValue placeholder="Select provider" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="max-h-64 overflow-y-auto">
                                {providersOptionsList.map((option) => (
                                  <SelectItem
                                    key={convertToString(option.value)}
                                    value={convertToString(option.value)}
                                    className={
                                      option.value === "OTHER"
                                        ? "border-t border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 font-medium"
                                        : ""
                                    }
                                  >
                                    {option.value === "OTHER" ? (
                                      <div className="flex items-center gap-2">
                                        <Plus className="h-4 w-4" />
                                        <span>{option.label}</span>
                                      </div>
                                    ) : (
                                      option.label
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={control}
                        name="rendering_provider_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                              Rendering Provider
                              <Tooltip text={TOOLTIPS.RENDERING_PROVIDER} />
                            </FormLabel>
                            <div className="space-y-3">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="useSameProvider"
                                  checked={useSameProvider}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setUseSameProvider(isChecked);
                                    if (!isChecked) {
                                      // Clear the rendering provider when unchecking
                                      setValue("rendering_provider_id", "");
                                    }
                                  }}
                                  className="h-4 w-4 text-[#005151] focus:ring-[#005151] border-gray-300 rounded"
                                />
                                <label
                                  htmlFor="useSameProvider"
                                  className="text-sm text-gray-600 cursor-pointer"
                                >
                                  Same as billing provider
                                </label>
                              </div>
                              {!useSameProvider && (
                                <Select
                                  onValueChange={field.onChange}
                                  value={safeStringValue(field.value)}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={`h-10 ${fieldState.error
                                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                        : ""
                                        }`}
                                    >
                                      <SelectValue placeholder="Select different provider" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent className="max-h-64 overflow-y-auto">
                                    {providersOptionsList.map((option) => (
                                      <SelectItem
                                        key={convertToString(option.value)}
                                        value={convertToString(option.value)}
                                        className={
                                          option.value === "OTHER"
                                            ? "border-t border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 font-medium"
                                            : ""
                                        }
                                      >
                                        {option.value === "OTHER" ? (
                                          <div className="flex items-center gap-2">
                                            <Plus className="h-4 w-4" />
                                            <span>{option.label}</span>
                                          </div>
                                        ) : (
                                          option.label
                                        )}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              {useSameProvider && (
                                <div className="h-10 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md flex items-center text-sm text-gray-500">
                                  Will use the same provider as billing provider
                                </div>
                              )}
                            </div>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Request Details Section */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-[#005151]" />
                    <CardTitle className="text-lg font-semibold text-gray-900">
                      Request Details
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <FormField
                      control={control}
                      name="type"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Request Type
                            <Tooltip text={TOOLTIPS.REQUEST_TYPE} />
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-10 ${fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                                  }`}
                              >
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-64 overflow-y-auto">
                              {filterValidOptions(requestTypes).map(
                                (option) => (
                                  <SelectItem
                                    key={convertToString(option.value)}
                                    value={convertToString(option.value)}
                                  >
                                    {option.label}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="desired_completion_date"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Results needed by
                            <Tooltip text={TOOLTIPS.DESIRED_COMPLETION_DATE} />
                          </FormLabel>
                          <FormControl>
                            <SafeDatePicker
                              value={field.value}
                              onChange={handleDateChange(field)}
                              placeholder="MM/DD/YYYY"
                              className="w-full"
                              error={!!fieldState.error}
                              onParseError={(error) => {
                                console.warn(
                                  "Date parsing error in desired_completion_date:",
                                  error
                                );
                              }}
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Conditional Fields for Eligibility Search */}
                  {typeField === "ELIGIBILITY_SEARCH" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <FormField
                        control={control}
                        name="place_of_service"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                              Place of Service
                              <Tooltip text={TOOLTIPS.PLACE_OF_SERVICE} />
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-10 ${fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                    }`}
                                >
                                  <SelectValue placeholder="Select place" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="max-h-64 overflow-y-auto">
                                {filterValidOptions(
                                  PLACE_OF_SERVICE_OPTIONS
                                ).map((option) => (
                                  <SelectItem
                                    key={convertToString(option.value)}
                                    value={convertToString(option.value)}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={control}
                        name="is_specialist"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                              Specialist
                              <Tooltip text={TOOLTIPS.IS_SPECIALIST} />
                            </FormLabel>
                            <Select
                              onValueChange={(value) => {
                                if (value === "true") field.onChange(true);
                                else if (value === "false")
                                  field.onChange(false);
                                else field.onChange(value);
                              }}
                              value={field.value?.toString()}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-10 ${fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                    }`}
                                >
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="max-h-64 overflow-y-auto">
                                {filterValidOptions(specialistOptions).map(
                                  (option) => (
                                    <SelectItem
                                      key={convertToString(option.value)}
                                      value={convertToString(option.value)}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Conditional Fields for Claims */}
                  {typeField === "CLAIMS" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <FormField
                        control={control}
                        name="claims_date_of_service"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                              Date of Service
                              <Tooltip text={TOOLTIPS.CLAIMS_DATE_OF_SERVICE} />
                            </FormLabel>
                            <FormControl>
                              <SafeDatePicker
                                value={field.value}
                                onChange={handleDateChange(field)}
                                placeholder="MM/DD/YYYY"
                                className="w-full"
                                error={!!fieldState.error}
                                onParseError={(error) => {
                                  console.warn(
                                    "Date parsing error in claims_date_of_service:",
                                    error
                                  );
                                }}
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={control}
                        name="billed_amount"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                              Billed Amount
                              <Tooltip text={TOOLTIPS.BILLED_AMOUNT} />
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="$0.00"
                                className={`h-10 ${fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                                  }`}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Call Script Selector */}
                  {typeField === "ELIGIBILITY_SEARCH" && (
                    <div className="mb-4">
                      <FormField
                        control={control}
                        name="call_script_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                              Call Script
                              <Tooltip
                                text="Select a call script that defines the questions our AI will ask during the insurance call."
                              />
                            </FormLabel>
                            <FormControl>
                              <div className="flex space-x-2">
                                <Select
                                  value={field.value || ""}
                                  onValueChange={field.onChange}
                                  disabled={loadingCallScripts}
                                >
                                  <SelectTrigger
                                    className={
                                      fieldState.error
                                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                        : ""
                                    }
                                  >
                                    <SelectValue placeholder={
                                      loadingCallScripts
                                        ? "Loading call scripts..."
                                        : callScripts.length === 0
                                        ? "No call scripts available"
                                        : "Select a call script..."
                                    } />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-64 overflow-y-auto">
                                    {callScripts.length === 0 ? (
                                      <div className="px-2 py-6 text-center text-sm text-gray-500">
                                        <p className="mb-2">No call scripts available.</p>
                                        <p className="text-xs">
                                          <Link
                                            to="/dashboard"
                                            className="text-[#005151] hover:underline"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              dispatch({ type: NEW_REQUEST_PAGE.hide });
                                              // Navigate to dashboard
                                              window.location.href = "/dashboard";
                                            }}
                                          >
                                            Create one in Account Settings
                                          </Link>
                                        </p>
                                      </div>
                                    ) : (
                                      callScripts.map((script) => (
                                        <SelectItem
                                          key={script.call_script_id || ""}
                                          value={script.call_script_id || ""}
                                        >
                                          <div className="flex items-center justify-between w-full">
                                            <span>{script.name || "Untitled Call Script"}</span>
                                            <Badge variant="outline" className="ml-2 text-xs">
                                              {script.questions.length} questions
                                            </Badge>
                                          </div>
                                        </SelectItem>
                                      ))
                                    )}
                                  </SelectContent>
                                </Select>
                                {field.value && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="default"
                                    onClick={() => setShowCallScriptPreview(true)}
                                    className="flex-shrink-0"
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Preview
                                  </Button>
                                )}
                              </div>
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                            {callScripts.length === 0 && !loadingCallScripts && (
                              <p className="text-sm text-gray-500 mt-1">
                                No call scripts available.{" "}
                                <Link
                                  to="/dashboard"
                                  className="text-[#005151] hover:underline"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    dispatch({ type: NEW_REQUEST_PAGE.hide });
                                    window.location.href = "/dashboard";
                                  }}
                                >
                                  Create one in Account Settings
                                </Link>
                              </p>
                            )}
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Procedure Codes for Prior Auth Status */}
                  {typeField === "PRIOR_AUTH_STATUS" && (
                    <div className="mb-4">
                      <FormField
                        control={control}
                        name="procedure_codes"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                              Procedure Codes
                              <Tooltip text={TOOLTIPS.PROCEDURE_CODES} />
                            </FormLabel>
                            <FormControl>
                              <TagInput
                                value={Array.isArray(field.value) ? field.value.filter((v): v is string => typeof v === 'string') : []}
                                onChange={field.onChange}
                                placeholder="Type a code and press Enter"
                                error={!!fieldState.error}
                                className={
                                  fieldState.error
                                    ? "border-red-500 focus-within:ring-red-500"
                                    : ""
                                }
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Diagnosis Codes for Medical/Mental Health */}
                  {typeField === "ELIGIBILITY_SEARCH" &&
                    (profileInfo?.specialty === "MEDICAL" ||
                      profileInfo?.specialty === "MENTAL_HEALTH") && (
                      <div className="mb-4">
                        <FormField
                          control={control}
                          name="diagnosis_codes"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                                Diagnosis Codes{" "}
                                <span className="text-gray-500">
                                  (optional)
                                </span>
                                <Tooltip text={TOOLTIPS.DIAGNOSIS_CODES} />
                              </FormLabel>
                              <FormControl>
                                <TagInput
                                  value={Array.isArray(field.value) ? field.value.filter((v): v is string => typeof v === 'string') : []}
                                  onChange={field.onChange}
                                  placeholder="Type a code and press Enter"
                                  error={!!fieldState.error}
                                  className={
                                    fieldState.error
                                      ? "border-red-500 focus-within:ring-red-500"
                                      : ""
                                  }
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 font-medium" />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                </CardContent>
              </Card>

              {/* Insurance & Patient Info - Combined */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border border-gray-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="h-4 w-4 text-[#005151]" />
                      <CardTitle className="text-lg font-semibold text-gray-900">
                        Insurance
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      <FormField
                        control={control}
                        name="insurance_name"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800">
                              Insurance Name
                            </FormLabel>
                            <FormControl>
                              <Combobox
                                options={payorOptions}
                                value={field.value}
                                onValueChange={field.onChange}
                                placeholder="Search payor name..."
                                searchPlaceholder="Search payor name..."
                                emptyMessage="No payor found."
                                className={
                                  fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />

                      {typeField === "ELIGIBILITY_SEARCH" && (
                        <FormField
                          control={control}
                          name="insurance_in_network"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-800">
                                Coverage
                              </FormLabel>
                              <Select
                                onValueChange={(value) => {
                                  if (value === "true") field.onChange(true);
                                  else if (value === "false")
                                    field.onChange(false);
                                  else if (value === "null")
                                    field.onChange(null);
                                  else field.onChange(value);
                                }}
                                value={field.value?.toString()}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={`h-10 ${fieldState.error
                                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                      : ""
                                      }`}
                                  >
                                    <SelectValue placeholder="Select coverage" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="max-h-64 overflow-y-auto">
                                  {filterValidOptions(
                                    typeField === "ELIGIBILITY_SEARCH"
                                      ? NETWORK_OPTIONAL
                                      : NETWORK
                                  ).map((option) => (
                                    <SelectItem
                                      key={convertToString(option.value)}
                                      value={convertToString(option.value)}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-red-500 font-medium" />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Patient Information */}
                <Card className="border border-gray-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-[#005151]" />
                      <CardTitle className="text-lg font-semibold text-gray-900">
                        Patient Info
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={control}
                          name="patient_name"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-800">
                                Patient Name
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter name"
                                  className={`h-10 ${fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                    }`}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 font-medium" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={control}
                          name="dob"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-800">
                                Date of Birth
                              </FormLabel>
                              <FormControl>
                                <SafeDatePicker
                                  value={field.value}
                                  onChange={handleDateChange(field)}
                                  placeholder="MM/DD/YYYY"
                                  className="w-full"
                                  error={!!fieldState.error}
                                  onParseError={(error) => {
                                    console.warn(
                                      "Date parsing error in dob:",
                                      error
                                    );
                                  }}
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 font-medium" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={control}
                          name="member_id"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-800">
                                Member ID/SSN
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="ID or SSN"
                                  className={`h-10 ${fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                    }`}
                                  {...field}
                                  value={safeStringValue(field.value)}
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 font-medium" />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={control}
                          name="group_id"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-800">
                                Group ID
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Group ID"
                                  className={`h-10 ${fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                    }`}
                                  {...field}
                                  value={safeStringValue(field.value)}
                                />
                              </FormControl>
                              <FormMessage className="text-red-500 font-medium" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={control}
                        name="patient_address"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800">
                              Address{" "}
                              <span className="text-gray-500">(optional)</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Patient address"
                                className={`h-10 ${fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                                  }`}
                                {...field}
                                value={safeStringValue(field.value)}
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Additional Information */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <Info className="h-4 w-4 text-[#005151]" />
                    <CardTitle className="text-lg font-semibold text-gray-900">
                      Additional Info
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <FormField
                    control={control}
                    name="additional_info"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                          Notes{" "}
                          <span className="text-gray-500">
                            {insuranceNameField === STEDI_PAYOR_OTHER
                              ? "(required — include payor name and a US phone number)"
                              : "(optional)"}
                          </span>
                          <Tooltip text={TOOLTIPS.ADDITIONAL_INFO} />
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={
                              insuranceNameField === STEDI_PAYOR_OTHER
                                ? "Payor name and phone number, e.g. Acme Health 555-123-4567"
                                : "Any additional information..."
                            }
                            className={`min-h-[80px] resize-none ${fieldState.error
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : ""
                              }`}
                            {...field}
                            value={safeStringValue(field.value)}
                          />
                        </FormControl>
                        <FormMessage className="text-red-500 font-medium" />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Submit Actions */}
              <div className="flex justify-end gap-3 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => dispatch({ type: NEW_REQUEST_PAGE.hide })}
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitDisabled}
                  className="bg-gradient-to-r from-[#005151] to-[#004040] hover:from-[#004040] hover:to-[#003030] text-white px-8 shadow-lg"
                >
                  {isSubmitDisabled ? (
                    <>
                      <Send className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>

          {/* New Practice/Provider Dialog */}
          <NewPracticeDialog
            open={showNewPracticeDialog}
            onOpenChange={setShowNewPracticeDialog}
            practiceId={currentPracticeId}
            refreshProfileInfo={async () => {
              if (refreshProfileInfo) {
                await refreshProfileInfo(false);
              }

              // Clear all practice/provider selections as requested
              setValue("practice_id", "");
              setValue("billing_provider_id", "");
              setValue("rendering_provider_id", "");
            }}
            currentUser={currentUser}
          />

          {/* Add Handler Dialog */}
          <AddHandlerDialog
            open={showAddHandlerDialog}
            onOpenChange={setShowAddHandlerDialog}
            refreshProfileInfo={() => {
              // Reload profile info if needed
              if (reloadPage) reloadPage(true);
            }}
          />

          {/* Call Script Preview Dialog */}
          {callScriptIdField && (
            <CallScriptPreviewDialog
              open={showCallScriptPreview}
              onOpenChange={setShowCallScriptPreview}
              callScriptId={callScriptIdField}
              onSelect={(id) => {
                setValue("call_script_id", id);
              }}
            />
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default NewRequestPage;
